import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import {
  DeviceDateRangeScheduleDto,
  DeviceDayScheduleDto,
  DeviceDto,
  DeviceService,
} from '@api-client';
import dayjs from 'dayjs';
import { ToastService as ToastServiceType } from '../../../services/toast.service';
import {
  Button,
  Hint,
  InputDatePicker,
  InputTime,
  Label,
  Select,
  Toggle,
} from '../../components';
import { DialogService } from '../../dialog/dialog.service';
import { injectClientStore } from './client-store-injector';

type ScheduleType = 'daterange' | 'day';

@Component({
  selector: 'app-create-device-schedule',
  imports: [
    CommonModule,
    Hint,
    Toggle,
    Select,
    Label,
    InputTime,
    Button,
    InputDatePicker,
  ],
  templateUrl: './device-schedule.component.html',
  styleUrl: './device-schedule.component.scss',
})
export class CreateDeviceScheduleComponent {
  private readonly deviceService = inject(DeviceService);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastServiceType);

  // ClientStore injection - 앱마다 다른 Store를 사용하도록 추상화
  private clientStore = injectClientStore();
  client = this.clientStore.client;

  device = input.required<DeviceDto>();

  // Toggle
  scheduleEnabled = signal<boolean>(false);
  timeActive = signal<boolean>(false);

  // Date / Day type
  scheduleType = signal<ScheduleType>('daterange');

  // Date Picker
  startDate = signal<Date>(new Date());
  endDate = signal<Date>(dayjs().endOf('day').toDate());
  seletedDays = signal<number[]>([]);

  // Time Picker
  startTime = signal<string>('00:00');
  endTime = signal<string>('00:00');

  /**
   * 시간 범위 유효성 여부
   * - startTime이 endTime보다 크거나 같으면 false
   * 예시 - startTime : 10:00 / endTime : 09:00 -> false
   */
  validTimeRange = signal<boolean>(true);

  selectOptions = [
    { label: '날짜', value: 'daterange' },
    { label: '요일', value: 'day' },
  ];

  dayItems = [
    { label: '일', value: 0 },
    { label: '월', value: 1 },
    { label: '화', value: 2 },
    { label: '수', value: 3 },
    { label: '목', value: 4 },
    { label: '금', value: 5 },
    { label: '토', value: 6 },
  ];

  private lastScheduleSignature = signal<string>('');

  constructor() {
    effect(() => {
      const schedule = this.device()?.schedule;
      if (!schedule) return;

      const signature = JSON.stringify(schedule);
      if (this.lastScheduleSignature() === signature) return;

      this.lastScheduleSignature.set(signature);
      this.scheduleEnabled.set(schedule.enabled);
      this.startTime.set(schedule.startTime ?? '00:00');
      this.endTime.set(schedule.endTime ?? '00:00');
      this.scheduleType.set(schedule.type as ScheduleType);
      this.timeActive.set(!!schedule.startTime && !!schedule.endTime);

      if (schedule.type === 'daterange') {
        const _schedule = schedule as DeviceDateRangeScheduleDto;
        this.startDate.set(dayjs(_schedule.startsAt).toDate());
        this.endDate.set(dayjs(_schedule.endsAt).toDate());
      } else {
        const _schedule = schedule as DeviceDayScheduleDto;
        this.seletedDays.set(_schedule.enabledDays);
      }
    });
  }

  seleteDay(day: number) {
    this.seletedDays.update((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
  }

  /**
   * 시간 범위 유효성 검증
   * @param startTime 시작 시간
   * @param endTime 종료 시간
   * @returns 유효성 여부
   */
  validateTimeRange(startTime: string, endTime: string): boolean {
    // 시간 형식 검증 (HH:MM)
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
      return false;
    }

    // 시간을 분으로 변환하여 비교
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    if (startMinutes >= endMinutes) {
      return false;
    }

    return true;
  }

  submit() {
    const deviceId = this.device()?.id;
    const client = this.client();

    if (!deviceId || !client) return;

    const selected = this.scheduleType();

    if (
      this.timeActive() &&
      !this.validateTimeRange(this.startTime(), this.endTime())
    ) {
      this.toastService.error('시작 시간은 종료 시간보다 빨라야 합니다.');
      return;
    }

    const body =
      selected === 'daterange'
        ? {
            enabled: this.scheduleEnabled(),
            type: 'daterange' as const,
            startsAt: dayjs(this.startDate()).startOf('day').toISOString(),
            endsAt: dayjs(this.endDate()).endOf('day').toISOString(),
            startTime: this.timeActive() ? this.startTime() : null,
            endTime: this.timeActive() ? this.endTime() : null,
          }
        : {
            enabled: this.scheduleEnabled(),
            type: 'day' as const,
            enabledDays: this.seletedDays(),
            startTime: this.timeActive() ? this.startTime() : null,
            endTime: this.timeActive() ? this.endTime() : null,
          };

    this.deviceService
      .deviceControllerSetSchedule({
        deviceId,
        clientWorkspaceId: client.id,
        body,
      })
      .subscribe(() => {
        this.toastService.success('스케줄이 설정되었습니다.');
        this.dialogService.sideDismiss(null, 'update');
      });
  }
}
